/**
 * The flow the landing page's own example builds.
 *
 * Pressing "Build it" with an empty box is not a mistake to correct, it is somebody saying
 * "show me". They get this: the brief that was sitting in the box as the placeholder, the
 * assistant's answer to it, and the finished flow, instantly and at no cost. Everything after
 * that is the real builder, so the next thing they type edits this flow like any other.
 *
 * It is deliberately not a plain list of questions, because a plain list is not what the
 * product does. It carries the shop's own picture, a question that appears only when it
 * applies (the child's age, for a kids cut), and a branch: "none of these suit me" leads
 * somewhere different from picking a time. Those are the three things people do not expect a
 * WhatsApp form to be able to do.
 *
 * The brief here is the landing page's placeholder, word for word
 * (landing-page/src/components/Hero.jsx). Change one and change the other: the conversation
 * shows the brief as though the visitor had typed it, and it would be a strange thing to show
 * them a sentence their box never offered.
 *
 * The JSON below is compiled output, what the builder itself produces from these screens,
 * If-wrapper, routing metadata and all. `exampleFlow.test.js` holds it to that: hydrate then
 * compile must return it unchanged, so this can never drift into something the builder would
 * not have written.
 */
import { SHOP_BANNER } from './exampleFlowImage';

export const EXAMPLE_ID = 'barber-bookings';

export const EXAMPLE_BRIEF =
  'Bookings for my barber shop: the cut they want, which barber, and a time that suits them';

/**
 * What the assistant says about it. Written as the assistant writes: what it built, what is
 * worth noticing, and the invitation to make it theirs.
 */
export const EXAMPLE_REPLY =
  'Here is a three-step booking: the cut, then the barber and a time, then their details. '
  + 'Two things worth a look: the child\u2019s age only appears if they pick a kids cut, and '
  + '\u201cnone of these suit me\u201d takes them down a different path where you just get their '
  + 'number. Tell me your services and opening times and I will swap them in, or send it to '
  + 'your phone and fill it in as a customer would.';

export const EXAMPLE_NAME = 'Barber shop bookings';

export const EXAMPLE_FLOW_JSON = {
  "version": "7.3",
  "screens": [
    {
      "id": "screen_a",
      "title": "Book a cut",
      "layout": {
        "type": "SingleColumnLayout",
        "children": [
          {
            "type": "Image",
            "src": SHOP_BANNER,
            "alt-text": "Marco & Dee barber shop",
            "scale-type": "cover",
            "height": 180
          },
          {
            "type": "TextHeading",
            "text": "Book your visit"
          },
          {
            "type": "TextBody",
            "text": "Three quick questions and you are booked in."
          },
          {
            "type": "RadioButtonsGroup",
            "name": "service",
            "label": "What are you after?",
            "required": true,
            "data-source": [
              {
                "id": "skin_fade",
                "title": "Skin fade (30 min)"
              },
              {
                "id": "cut_beard",
                "title": "Cut & beard trim (45 min)"
              },
              {
                "id": "beard",
                "title": "Beard trim (15 min)"
              },
              {
                "id": "kids",
                "title": "Kids cut (20 min)"
              }
            ]
          },
          {
            "type": "If",
            "condition": "${form.service} == 'kids'",
            "then": [
              {
                "type": "TextInput",
                "name": "child_age",
                "label": "Child's age",
                "input-type": "number",
                "required": false
              }
            ]
          },
          {
            "type": "Footer",
            "label": "Continue",
            "on-click-action": {
              "name": "navigate",
              "next": {
                "type": "screen",
                "name": "screen_b"
              }
            }
          }
        ]
      }
    },
    {
      "id": "screen_b",
      "title": "Barber and time",
      "layout": {
        "type": "SingleColumnLayout",
        "children": [
          {
            "type": "TextHeading",
            "text": "Who with, and when?"
          },
          {
            "type": "RadioButtonsGroup",
            "name": "barber",
            "label": "Barber",
            "required": true,
            "data-source": [
              {
                "id": "any",
                "title": "Whoever is free"
              },
              {
                "id": "marco",
                "title": "Marco"
              },
              {
                "id": "dee",
                "title": "Dee"
              }
            ]
          },
          {
            "type": "RadioButtonsGroup",
            "name": "slot",
            "label": "Saturday",
            "required": true,
            "data-source": [
              {
                "id": "s1030",
                "title": "10:30"
              },
              {
                "id": "s1300",
                "title": "13:00"
              },
              {
                "id": "s1615",
                "title": "16:15"
              },
              {
                "id": "none",
                "title": "None of these suit me"
              }
            ]
          },
          {
            "type": "If",
            "condition": "${form.slot} == 'none'",
            "then": [
              {
                "type": "Footer",
                "label": "Continue",
                "on-click-action": {
                  "name": "navigate",
                  "next": {
                    "type": "screen",
                    "name": "screen_d"
                  }
                }
              }
            ],
            "else": [
              {
                "type": "Footer",
                "label": "Continue",
                "on-click-action": {
                  "name": "navigate",
                  "next": {
                    "type": "screen",
                    "name": "screen_c"
                  }
                }
              }
            ]
          }
        ]
      }
    },
    {
      "id": "screen_c",
      "title": "Your details",
      "layout": {
        "type": "SingleColumnLayout",
        "children": [
          {
            "type": "TextInput",
            "name": "full_name",
            "label": "Your name",
            "input-type": "text",
            "required": true
          },
          {
            "type": "TextInput",
            "name": "phone",
            "label": "Mobile number",
            "input-type": "phone",
            "required": true
          },
          {
            "type": "OptIn",
            "name": "reminder",
            "label": "Text me a reminder the day before",
            "required": false
          },
          {
            "type": "Footer",
            "label": "Confirm booking",
            "on-click-action": {
              "name": "complete",
              "payload": {
                "service": "${screen.screen_a.form.service}",
                "child_age": "${screen.screen_a.form.child_age}",
                "barber": "${screen.screen_b.form.barber}",
                "slot": "${screen.screen_b.form.slot}",
                "full_name": "${form.full_name}",
                "phone": "${form.phone}",
                "reminder": "${form.reminder}"
              }
            }
          }
        ]
      },
      "terminal": true
    },
    {
      "id": "screen_d",
      "title": "We will call you",
      "layout": {
        "type": "SingleColumnLayout",
        "children": [
          {
            "type": "TextBody",
            "text": "No problem. Leave your number and we will call with more times."
          },
          {
            "type": "TextInput",
            "name": "full_name",
            "label": "Your name",
            "input-type": "text",
            "required": true
          },
          {
            "type": "TextInput",
            "name": "phone",
            "label": "Mobile number",
            "input-type": "phone",
            "required": true
          },
          {
            "type": "Footer",
            "label": "Ask them to call me",
            "on-click-action": {
              "name": "complete",
              "payload": {
                "service": "${screen.screen_a.form.service}",
                "child_age": "${screen.screen_a.form.child_age}",
                "barber": "${screen.screen_b.form.barber}",
                "slot": "${screen.screen_b.form.slot}",
                "full_name": "${form.full_name}",
                "phone": "${form.phone}"
              }
            }
          }
        ]
      },
      "terminal": true
    }
  ],
  "routing_model": {
    "screen_a": [
      "screen_b"
    ],
    "screen_b": [
      "screen_d",
      "screen_c"
    ]
  },
  "_builderMeta": {
    "screenRouting": {
      "screen_a": {
        "mode": "simple",
        "rules": [],
        "defaultNextScreenId": "screen_b"
      },
      "screen_b": {
        "mode": "simple",
        "rules": [
          {
            "mode": "simple",
            "fieldName": "slot",
            "operator": "eq",
            "value": "none",
            "nextScreenId": "screen_d"
          }
        ],
        "defaultNextScreenId": "screen_c"
      }
    },
    "componentConditions": {
      "screen_a:4": {
        "mode": "simple",
        "field": "service",
        "operator": "eq",
        "value": "kids"
      }
    }
  }
};
